"use client";

import { captureException } from "@sentry/nextjs";
import { Component, type ReactNode, useEffect } from "react";

interface SlotExperienceBoundaryProps {
	slug: string;
	children: ReactNode;
}

interface SlotExperienceBoundaryState {
	error: Error | null;
}

/**
 * Reporting happens here rather than in `componentDidCatch` so the Sentry call is
 * an effect of the fallback render, not a side effect of the commit that unmounted
 * the failing tree.
 */
const SlotFailureReport = ({ slug, error }: { slug: string; error: Error }) => {
	useEffect(() => {
		captureException(error, { tags: { slot_experience_slug: slug } });
	}, [slug, error]);

	return null;
};

/**
 * Chrome renders outside the page's own error boundary, so a throw while hydrating
 * an authored footer would blank the whole document. This drops the footer instead.
 *
 * It cannot fall back to the legacy footer: by the time it fires the server has
 * already committed the authored one.
 */
export default class SlotExperienceBoundary extends Component<
	SlotExperienceBoundaryProps,
	SlotExperienceBoundaryState
> {
	state: SlotExperienceBoundaryState = { error: null };

	static getDerivedStateFromError(error: Error): SlotExperienceBoundaryState {
		return { error };
	}

	render() {
		if (this.state.error) {
			return <SlotFailureReport slug={this.props.slug} error={this.state.error} />;
		}

		return this.props.children;
	}
}
